import { Device } from "@twilio/voice-sdk";
import { ApiClient } from "../utils/apiClient";
import config from "../utils/config";

// Create a dedicated API client for voice services
// This allows using a different base URL for voice services if needed
const voiceApiClient = new ApiClient(config.voiceApiBaseUrl);

class VoiceService {
  constructor() {
    this.device = null;
    this.token = null;
  }

  /**
   * Check if the browser is supported for Twilio Voice
   * @returns {boolean}
   */
  isBrowserSupported() {
    // Device.isSupported is a boolean property in Twilio Voice SDK v2+
    return Device.isSupported;
  }

  /**
   * Fetch Twilio Access Token from backend
   * @returns {Promise<string>} The access token
   */
  async fetchToken() {
    if (!config.features.enableVoice) {
      throw new Error("Voice feature is disabled.");
    }
    try {
      const response = await voiceApiClient.post("/api/voice/token");

      if (!response) {
        throw new Error("No response from voice API");
      }

      if (!response.ok) {
        // Create a detailed error object
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to fetch token: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      if (!data.token) {
        throw new Error("Token missing from response");
      }
      return data.token;
    } catch (error) {
      console.error("Error fetching Twilio token:", error);
    
      const normalizedError =
        error instanceof Error
          ? error
          : new Error(error?.message || "Failed to fetch voice token");
    
      throw normalizedError;
    }
  }

  /**
   * Initialize the Twilio Device with an Access Token
   * @param {string} token - The access token from the backend
   * @param {Object} options - Additional options for the Device
   */
  initialize(token, options = {}) {
    console.log("VoiceService: Initializing device with token...");
    if (!config.features.enableVoice) {
      console.warn("VoiceService: Voice feature is disabled in configuration.");
      return null;
    }

    if (!this.isBrowserSupported()) {
      console.warn("VoiceService: Browser not supported for Twilio Voice.");
      return null;
    }

    if (!token) {
      throw new Error(
        "VoiceService: Twilio token is required for initialization",
      );
    }

    this.token = token;

    // Initialize the device
    if (this.device) {
      console.log("VoiceService: Destroying existing device instance...");
      this.device.destroy();
    }

    console.log("VoiceService: Creating new Twilio Device instance...");
    this.device = new Device(token, {
      logLevel: 2, // Increased for better debugging (1 = errors, 2 = warnings, 3 = info)
      codecPreferences: ["opus", "pcmu"],
      ...options,
    });

    // Register the device to receive incoming calls
    console.log("VoiceService: Registering device...");
    this.device.register();

    // Handle token expiration
    this.device.on("tokenWillExpire", async () => {
      console.log("Twilio token will expire soon, refreshing...");
      try {
        const newToken = await this.fetchToken();
        this.device.updateToken(newToken);
        this.token = newToken;
        console.log("Twilio token refreshed successfully");
      } catch (error) {
        console.error("Failed to refresh Twilio token:", error);
      }
    });

    return this.device;
  }

  /**
   * Make an outgoing call
   * @param {Object} optionsOrParams - Twilio connect options or legacy params.
   *   Correct Twilio pattern: { params: { To: "+123..." , ... } }
   *   Legacy support: { To: "+123..." , ... } (will be wrapped into { params }).
   */
  async connect(optionsOrParams = {}) {
    if (!this.device) {
      throw new Error("Device not initialized");
    }

    // Check if device state is effectively ready or we need to check token?
    // The device handles token validity, but we can ensure we are registered.

    const connectOptions =
      optionsOrParams && typeof optionsOrParams === "object" && "params" in optionsOrParams
        ? optionsOrParams
        : { params: optionsOrParams || {} };

    try {
      return await this.device.connect(connectOptions);
    } catch (err) {
      const normalizedError =
        err instanceof Error
          ? err
          : new Error(err?.message || "Twilio connection failed");
    
      throw normalizedError;
    }
  }

  /**
   * Disconnect all active connections
   */
  disconnectAll() {
    if (this.device) {
      this.device.disconnectAll();
    }
  }

  /**
   * Destroy the device instance (cleanup)
   */
  destroy() {
    if (this.device) {
      this.device.destroy();
      this.device = null;
      this.token = null;
    }
  }

  /**
   * Get the current device instance
   */
  getDevice() {
    return this.device;
  }
}

// Export as a singleton
const voiceService = new VoiceService();
export default voiceService;
